import type { ActivityTypeOption } from './useActivityTypeOptions';

/** Sottoinsieme di `ActivityCustomType` (activityTypesStore.ts) richiesto dal merge. */
export interface ActivityCustomTypeLike {
  slug: string;
  name: string;
  active: boolean;
}

export interface ActivityTypeSelectGroups {
  standardOptions: ActivityTypeOption[];
  /** Custom attivi + (se selezionato) il custom disattivato in coda. */
  customOptions: ActivityTypeOption[];
  /**
   * true quando `selectedType` è proprio il tipo custom disattivato reintrodotto in coda
   * a `customOptions` (T3 redesign: la select "Tipo" mostra il valore selezionato in
   * colore secondario per segnalarne lo stato, invece del solo suffisso testuale).
   */
  selectedIsInactiveCustom: boolean;
}

/**
 * Divide l'output flat di `useActivityTypeOptions()` (T7: system options seguite dai
 * custom types attivi, nello stesso ordine) in due gruppi per la select "Tipo": standard
 * di sistema e custom per-org, per replicare il divider "──── Tipi personalizzati ────"
 * della fonte (LeadStatusUpdateDrawer.tsx:1990-1999).
 *
 * EDGE CASE VINCOLANTE (fonte :238-245, NON coperto da useActivityTypeOptions — la
 * fonte lo segnala esplicitamente nel proprio doc comment): se `selectedType` è lo slug
 * di un tipo custom nel frattempo DISATTIVATO dall'admin, va reintrodotto in coda a
 * `customOptions` (etichetta con suffisso "(disattivato)") per evitare che la select
 * mostri un valore selezionato assente dalle option — mismatch/selezione vuota.
 *
 * Funzione pura: nessuna query, testabile senza store/DOM (vedi activityTypeSelect.test.ts).
 */
export function buildActivityTypeSelectGroups(
  typeOptions: ActivityTypeOption[],
  customTypes: ActivityCustomTypeLike[],
  selectedType: string
): ActivityTypeSelectGroups {
  const activeCustomCount = customTypes.filter((ct) => ct.active).length;
  const standardOptions = typeOptions.slice(0, typeOptions.length - activeCustomCount);
  const activeCustomOptions = typeOptions.slice(typeOptions.length - activeCustomCount);

  const selectedInactiveCustomType = customTypes.find((ct) => !ct.active && ct.slug === selectedType);
  const customOptions = selectedInactiveCustomType
    ? [
        ...activeCustomOptions,
        {
          value: selectedInactiveCustomType.slug,
          label: `${selectedInactiveCustomType.name} (disattivato)`,
          icon: null,
        },
      ]
    : activeCustomOptions;

  return { standardOptions, customOptions, selectedIsInactiveCustom: !!selectedInactiveCustomType };
}
